import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useLang } from "../i18n/LanguageContext";
import "../styles/styles.css";

const FLAGS      = { es: "🇪🇸", en: "🇬🇧", pt: "🇧🇷", fr: "🇫🇷", de: "🇩🇪" };
const LANG_NAMES = { es: "ES", en: "EN", pt: "PT", fr: "FR", de: "DE" };

const Navbar = ({ user, onLogout }) => {
  const { lang, setLang, tr, SUPPORTED } = useLang();
  const navigate = useNavigate();
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [langOpen,    setLangOpen]    = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchQ,     setSearchQ]     = useState("");
  const profileRef = useRef(null);

  const isCreator = user?.role === "creator";
  const isAdmin   = user?.role === "admin" && user.email === "monteroesteban@hotmail.com";

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const close = () => { setMenuOpen(false); setLangOpen(false); setProfileOpen(false); };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQ.trim())}`);
      setSearchQ("");
      close();
    }
  };

  const handleLogout = () => {
    close();
    onLogout && onLogout();
    navigate("/");
  };

  const avatarLetter = user ? (user.username || user.email).charAt(0).toUpperCase() : "?";

  return (
    <nav className="navbar">
      <Link to="/" className="nav-logo" onClick={close}>
        <Logo />
        <span className="nav-logo-text">AIVEO</span>
      </Link>

      <form className="nav-search-form" onSubmit={handleSearch}>
        <input
          className="nav-search-input"
          type="search"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder={lang === "en" ? "Search..." : "Buscar..."}
        />
        <button type="submit" className="nav-search-btn" aria-label="Search">🔍</button>
      </form>

      <button className="nav-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
        {menuOpen ? "✕" : "☰"}
      </button>

      <div className={`nav-links ${menuOpen ? "nav-links--open" : ""}`}>
        <Link to="/"         onClick={close}>{tr.nav.home}</Link>
        <Link to="/movies"   onClick={close}>{tr.nav.movies}</Link>
        <Link to="/books"    onClick={close}>{tr.nav.books}</Link>
        <Link to="/comics"   onClick={close}>{tr.nav.comics}</Link>
        <Link to="/creators" onClick={close}>{tr.nav.creators}</Link>
      </div>

      <div className="nav-right">

        <div className="lang-selector" onMouseLeave={() => setLangOpen(false)}>
          <button className="lang-current" onClick={() => setLangOpen(!langOpen)}>
            {FLAGS[lang]} {LANG_NAMES[lang]} ▾
          </button>
          {langOpen && (
            <div className="lang-dropdown">
              {SUPPORTED.map((l) => (
                <button
                  key={l}
                  className={`lang-option ${l === lang ? "active" : ""}`}
                  onClick={() => { setLang(l); setLangOpen(false); }}
                >
                  {FLAGS[l]} {LANG_NAMES[l]}
                </button>
              ))}
            </div>
          )}
        </div>

        {user ? (
          <div className="nav-profile-wrap" ref={profileRef}>
            <button
              className={`nav-avatar-btn ${profileOpen ? "active" : ""}`}
              onClick={() => setProfileOpen(!profileOpen)}
            >
              <span className="nav-avatar">{avatarLetter}</span>
              <span className="nav-avatar-chevron">▾</span>
            </button>

            {profileOpen && (
              <div className="nav-profile-dropdown">
                <div className="npd-header">
                  <span className="npd-avatar">{avatarLetter}</span>
                  <div>
                    <p className="npd-name">{user.username || user.email.split("@")[0]}</p>
                    <p className="npd-email">{user.email}</p>
                  </div>
                </div>

                <div className="npd-divider" />

                <Link to="/profile" className="npd-item" onClick={close}>
                  👤 {tr.nav.profile || "Mi perfil"}
                </Link>

                {(isCreator || isAdmin) && (
                  <Link to="/upload" className="npd-item" onClick={close}>
                    ⬆ {tr.nav.upload}
                  </Link>
                )}

                {isAdmin && (
                  <>
                    <div className="npd-divider" />
                    <p className="npd-section">Administración</p>
                    <Link to="/admin/submissions" className="npd-item" onClick={close}>
                      📋 {tr.nav.pitches}
                    </Link>
                    <Link to="/admin/users" className="npd-item" onClick={close}>
                      👥 {tr.nav.users}
                    </Link>
                    <Link to="/admin/videos" className="npd-item" onClick={close}>
                      🎬 {tr.nav.videos}
                    </Link>
                  </>
                )}

                <div className="npd-divider" />

                <button className="npd-item npd-logout" onClick={handleLogout}>
                  ✕ {tr.nav.logout || "Cerrar sesión"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="nav-auth-btns">
            <Link to="/login"    onClick={close}>{tr.nav.login}</Link>
            <Link to="/register" className="btn-register-nav" onClick={close}>{tr.nav.register}</Link>
          </div>
        )}

      </div>
    </nav>
  );
};

export default Navbar;
